// ============================================
// 景品マスター・発注 (prize_masters, prize_orders)
// ============================================
import { supabase } from '../lib/supabase'
import { getCache, setCache, clearCache, supName, SUPPLIER_MAP } from './utils'
import { writeAuditLog } from './audit'
import { CHANGE_ORG_ID } from '../lib/auth/orgConstants'
import { z } from 'zod'
import { PrizeMasterRowSchema } from './schemas/index.js'

// prize_masters の getPrizeMasters 用部分スキーマ (INC-005: SELECT列不一致防止)
const PrizeMasterSearchSchema = PrizeMasterRowSchema.pick({
  prize_id: true, prize_name: true, original_cost: true, category: true,
}).array()

// 景品名検索用リスト（prize_id + prize_name + original_cost）
export async function getPrizeMasters() {
  const KEY = 'prize_masters_search_v2'
  if (getCache(KEY)) return getCache(KEY)
  // eslint-disable-next-line supabase-guard/require-bound -- LEGACY(D-330): 是正保留
  const { data, error } = await supabase
    .from('prize_masters')
    .select('prize_id, prize_name, original_cost, category')
    // SPEC-PRIZE-MASTER-STATUS-DEPRECATE-01: status='active' → phase!='dead' (廃番のみ除外)
    .neq('phase', 'dead')
    .order('prize_name')
  if (error) { console.error('prize_masters取得エラー:', error.message); return [] }
  const _prizesParsed = PrizeMasterSearchSchema.safeParse(data ?? [])
  const result = _prizesParsed.success ? _prizesParsed.data : (data ?? [])
  setCache(KEY, result)
  return result
}

export async function getPrizes() {
  if (getCache('prizes')) return getCache('prizes')
  // eslint-disable-next-line supabase-guard/require-bound -- LEGACY(D-330): 是正保留
  const { data, error } = await supabase.from('prize_masters').select('*').order('prize_id')
  if (error) throw new Error('景品取得エラー: ' + error.message)
  const result = (data || []).map(r => ({
    _id: r.prize_id,
    prize_id: r.prize_id, prize_name: r.prize_name, jan_code: r.jan_code || '',
    unit_cost: String(r.original_cost || '0'),
    supplier_name: supName(r.supplier_id), supplier_id: r.supplier_id || '',
    // SPEC-PRIZE-MASTER-STATUS-DEPRECATE-01: status 廃止、phase!='dead' を「稼働中」と判定
    supplier_contact: '', is_active: r.phase !== 'dead' ? 'TRUE' : 'FALSE',
    created_at: r.created_at || '', updated_at: r.updated_at || '',
    image_url: r.image_url || null,
    short_name: r.short_name || r.prize_name || '', item_size: r.size || '', category: r.category || '',
    order_at: r.order_date || '', arrival_at: r.expected_date || '',
    restock_count: '', stock_count: '',
    case_count: '', pieces_per_case: String(r.default_case_quantity || ''),
    aliases: r.aliases || '', notes: r.notes || '',
  }))
  setCache('prizes', result)
  return result
}

export async function addPrize(p) {
  const supEntry = Object.entries(SUPPLIER_MAP).find(([, v]) => v === p.supplier_name)
  const supId = supEntry ? supEntry[0] : p.supplier_id || null
  const { data, error } = await supabase.from('prize_masters').insert({
    prize_id: p.prize_id || undefined,
    prize_name: p.prize_name,
    original_cost: parseInt(p.unit_cost) || 0,
    supplier_id: supId,
    supplier_name: p.supplier_name || null,
    jan_code: p.jan_code || null,
    category: p.category || null,
    size: p.item_size || null,
    default_case_quantity: parseInt(p.pieces_per_case) || null,
    // SPEC-PRIZE-MASTER-STATUS-DEPRECATE-01: status 列廃止、phase で表現
    phase: p.is_active === 'FALSE' ? 'dead' : 'active',
    notes: p.notes || null,
    organization_id: CHANGE_ORG_ID,
  }).select().single()
  if (error) throw new Error('景品登録エラー: ' + error.message)
  clearCache()

  writeAuditLog({
    action: 'master_create',
    target_table: 'prize_masters',
    target_id: data.prize_id,
    detail: `景品登録: ${p.prize_name}`,
    staff_id: p.created_by || '',
  })

  return data.prize_id
}

export async function getPrizeOrders() {
  if (getCache('prize_orders')) return getCache('prize_orders')
  // eslint-disable-next-line supabase-guard/require-bound -- LEGACY(D-330): 是正保留
  const { data, error } = await supabase.from('prize_orders').select('*').order('order_date', { ascending: false })
  if (error) throw new Error('発注取得エラー: ' + error.message)
  const result = (data || []).map(r => ({
    _id: r.order_id,
    order_id: r.order_id, prize_id: r.prize_id || '', prize_name: r.prize_name_raw || '',
    order_date: r.order_date || '', order_quantity: String(r.pieces_per_case || ''), // D-492
    arrived_at: r.arrived_at || '', arrival_quantity: String(r.received_quantity || ''),
    unit_cost_at_order: String(r.unit_cost || '0'),
    total_cost: String((r.unit_cost || 0) * (r.pieces_per_case || 0)), // D-492
    note: r.notes || '', created_at: r.created_at || '',
    supplier_name: supName(r.supplier_id),
    status: r.status || '',
    case_count: String(r.case_count || ''),
    case_cost: String(r.case_cost || ''),
    expected_date: r.expected_date || '',
    destination: r.destination || '',
    pieces_per_case: r.pieces_per_case != null ? String(r.pieces_per_case) : '',
    shipping_cost: String(r.shipping_cost || '0'),
    total_tax_included: String(r.total_tax_included || '0'),
  }))
  setCache('prize_orders', result)
  return result
}

export async function markOrderArrived(orderId, arrivedQuantity, staffId, { reason_code, reason_note } = {}) {
  const now = new Date().toISOString()
  const { error } = await supabase.from('prize_orders').update({
    arrived_at: now,
    received_quantity: arrivedQuantity,
    status: 'arrived',
  }).eq('order_id', orderId)
  if (error) throw new Error('入荷更新エラー: ' + error.message)
  clearCache()

  writeAuditLog({
    action: 'order_arrived',
    target_table: 'prize_orders',
    target_id: orderId,
    detail: `入荷確認: ${arrivedQuantity}個`,
    staff_id: staffId || undefined,
    reason_code: reason_code || 'ARRIVAL',
    reason_note: reason_note || undefined,
  })
}

export async function updateOrder(orderId, patch, staffId) {
  // D-492: case_quantity は廃止。更新経路からも外す(新規書き込みを構造的に不可能にする)。
  const allowed = ['pieces_per_case', 'unit_cost', 'case_cost', 'shipping_cost', 'notes']
  const updates = {}
  for (const key of allowed) {
    if (patch[key] !== undefined) updates[key] = patch[key]
  }
  if (Object.keys(updates).length === 0) return
  const { error } = await supabase.from('prize_orders').update(updates).eq('order_id', orderId)
  if (error) throw new Error('発注更新エラー: ' + error.message)
  clearCache()

  writeAuditLog({
    action: 'order_update',
    target_table: 'prize_orders',
    target_id: orderId,
    detail: `発注編集: ${Object.keys(updates).join(', ')}`,
    staff_id: staffId || undefined,
  })
}

// ============================================
// SPEC-PRIZE-IMAGE-MANUAL-UPLOAD-01 (D-218): 景品マスタへの写真手貼り
// ============================================

function scaleDown(w, h, maxEdge) {
  if (w <= maxEdge && h <= maxEdge) return { width: w, height: h }
  const scale = maxEdge / Math.max(w, h)
  return { width: Math.round(w * scale), height: Math.round(h * scale) }
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    try { canvas.toBlob((b) => resolve(b), type, quality) }
    catch { resolve(null) }
  })
}

/**
 * CLIENT_ENCODE: アップロード前にブラウザ側で必ず JPEG へ再エンコードする。
 *   createImageBitmap(imageOrientation:'from-image') → canvas → toBlob('image/jpeg')。
 *   長辺は maxEdge(=1600)px 上限に縮小、品質は 0.85。
 *   SIZE_GUARD: 1.5MB 超なら品質 0.7 で再試行。それでも 3MB 超はエラーで中断(黙って失敗させない)。
 *   狙いは HEIC を上げさせない・EXIF(位置情報)を落とす・原寸数MBを上げさせない(ENCODE_REASON)。
 * @returns {Promise<{ blob: Blob, width: number, height: number }>}
 */
export async function encodePrizeImageToJpeg(file, { maxEdge = 1600, quality = 0.85 } = {}) {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  const { width, height } = scaleDown(bitmap.width, bitmap.height, maxEdge)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas 2d context 取得失敗')
  ctx.drawImage(bitmap, 0, 0, width, height)
  bitmap.close?.()

  let blob = await canvasToBlob(canvas, 'image/jpeg', quality)
  // SIZE_GUARD: 1.5MB 超は品質を下げて再試行
  if (blob && blob.size > 1.5 * 1024 * 1024) {
    blob = await canvasToBlob(canvas, 'image/jpeg', 0.7)
  }
  if (!blob) throw new Error('画像を JPEG に変換できませんでした（HEIC 等の可能性）。別の写真をお試しください')
  if (blob.size > 3 * 1024 * 1024) {
    throw new Error(`画像が大きすぎます（${(blob.size / 1024 / 1024).toFixed(1)}MB）。別の写真を選んでください`)
  }
  return { blob, width, height }
}

/**
 * 景品マスタに写真を手貼りする。
 *   ①JPEG再エンコード → ②announcements/manual/ へアップロード(上書きしない新パス) → ③prize_masters.image_url 更新。
 *   PATH_RULE: manual/{prize_id}_{epoch秒}.jpg。UPLOAD_FAIL: アップロードが先・DB更新が後(逆にすると存在しないパスを指す行が生まれる)。
 *   image_url はバケット名を含めず manual/ から始まる相対パス(sgp/ 形式と揃え、表示側 resolveImageUrl が公開URL化)。
 * @returns {Promise<{ data: object|null, error: Error|null }>}
 */
export async function uploadPrizeImage(prizeId, file, staffId) {
  try {
    const { blob } = await encodePrizeImageToJpeg(file)
    const path = `manual/${prizeId}_${Math.floor(Date.now() / 1000)}.jpg`
    const { error: upErr } = await supabase.storage
      .from('announcements')
      .upload(path, blob, { contentType: 'image/jpeg', upsert: false })
    if (upErr) return { data: null, error: upErr }

    const { data, error } = await supabase
      .from('prize_masters')
      .update({ image_url: path, updated_at: new Date().toISOString(), updated_by: staffId || null })
      .eq('prize_id', prizeId)
      .select('prize_id, image_url')
      .single()
    if (error) return { data: null, error }
    clearCache()
    return { data, error: null }
  } catch (e) {
    return { data: null, error: e instanceof Error ? e : new Error(String(e)) }
  }
}

// SPEC-PRIZE-PROVISIONAL-REGISTER-V1 (D-785): 仮登録された景品を「正式にする」/「廃番にする」。
//   ★`updatePrizeMaster` の allowed を広げなかった理由: あれは short_name 1 列だけを許す関数で、
//     phase を足すと **どの呼び出し元からでも phase を書ける**ようになる。
//     ここでは行き先を 2 値に閉じた専用の関数にする(D-215: 目的ごとに口を 1 つ)。
//   ★RLS は 1 行も広げていない。UPDATE は既存の admin_update(admin / manager)のまま。
//     → staff がこれを呼んでも RLS が弾く。画面側でも manager 以上にしか出さない(3 層)。
//   ★phase 以外の列に触れない(D-785 AC19)。統合(merged_into_prize_id)にも触れない(D-786)。
export const PRIZE_PHASE = { ACTIVE: 'active', DEAD: 'dead' }

/** phase の書き換えで使う監査の区分。★AUDIT_REASONS は広げない(共有の語彙を増やさない)。 */
export const PRIZE_PHASE_REASON = {
  [PRIZE_PHASE.ACTIVE]: 'PRIZE_PROVISIONAL_PROMOTE',
  [PRIZE_PHASE.DEAD]:   'PRIZE_PROVISIONAL_RETIRE',
}

/**
 * 景品マスタの phase を 'active' / 'dead' のどちらかに書き換える。
 * ★それ以外の値は受け付けない(yobigun / normal / provisional へは戻せない)。
 * @param {string} prizeId
 * @param {'active'|'dead'} phase
 * @param {string} [staffId]
 * @returns {Promise<{ ok: boolean, error: string|null }>} ★投げない。画面が文言に落とす(D-546)。
 */
export async function setPrizePhase(prizeId, phase, staffId) {
  const id = String(prizeId ?? '').trim()
  if (id === '') return { ok: false, error: 'prize_id が空です' }
  if (phase !== PRIZE_PHASE.ACTIVE && phase !== PRIZE_PHASE.DEAD) {
    return { ok: false, error: `phase が不正です: ${String(phase)}` }
  }
  // ★書くのは phase 1 列だけ。
  const { error } = await supabase.from('prize_masters').update({ phase }).eq('prize_id', id)
  if (error) return { ok: false, error: error.message }
  clearCache()

  writeAuditLog({
    action: 'master_update',
    target_table: 'prize_masters',
    target_id: id,
    detail: `仮登録の景品を ${phase === PRIZE_PHASE.ACTIVE ? '正式化' : '廃番化'}: phase=${phase}`,
    reason_code: PRIZE_PHASE_REASON[phase],
    after_data: { phase },
    staff_id: staffId || undefined,
  })
  return { ok: true, error: null }
}

export async function updatePrizeMaster(prizeId, patch, staffId) {
  const allowed = ['short_name']
  const updates = {}
  for (const key of allowed) {
    if (patch[key] !== undefined) updates[key] = patch[key]
  }
  if (Object.keys(updates).length === 0) return
  const { error } = await supabase.from('prize_masters').update(updates).eq('prize_id', prizeId)
  if (error) throw new Error('景品マスタ更新エラー: ' + error.message)
  clearCache()

  writeAuditLog({
    action: 'master_update',
    target_table: 'prize_masters',
    target_id: prizeId,
    detail: `マスタ編集: ${Object.keys(updates).join(', ')}`,
    staff_id: staffId || undefined,
  })
}
